import React from 'react';
import * as Form from 'react-hook-form';
import { Keyboard } from 'react-native';
import { t } from '@lingui/macro';
import { useNavigation, useRoute } from '@react-navigation/native';

import RecoveryValidateToken from '@src/api/RecoveryValidateToken';
import useAxiosErrorHandler from '@src/hooks/useAxiosErrorHandler';
import useSnackBar from '@src/hooks/useSnackBar';
import { i18n, i18yup } from '@src/i18n';

import { FormData, ScreenNavigationProp, ScreenRouteProp } from './types';

const useForm = () => {
  const route = useRoute<ScreenRouteProp>();
  const navigation = useNavigation<ScreenNavigationProp>();
  const snackBar = useSnackBar();
  const axiosErrorHandler = useAxiosErrorHandler();
  const [loading, setLoading] = React.useState(false);
  const form = Form.useForm<FormData>({
    validationSchema: i18yup.object<FormData>({
      token: i18yup
        .string()
        .matches(/^[0-9]+$/, i18n._(t`Must contain only digits`))
        .required()
        .label(i18n._(t`Token`)),
    }),
  });

  const handleSubmit = React.useCallback(
    async ({ token }: FormData) => {
      setLoading(true);

      try {
        Keyboard.dismiss();
        const response = await RecoveryValidateToken({
          preToken: route.params.preToken,
          token,
        });
        if (response.data?.result === 'SUCCESS') {
          navigation.navigate('ResetPassword', {
            preToken: route.params.preToken,
            token: response.data.token,
            username: response.data.username,
          });
        }
      } catch (e) {
        axiosErrorHandler(e);
        snackBar.show({ content: e?.message });

        // FIXME: when error handled properly from server
        // change this section with better error messages
        // if ((e?.message as string).includes('400')) {
        //   console.log(JSON.stringify(e, undefined, 2));
        //   snackBar.show({ content: i18n._(t`Invalid code`) });
        // } else {
        //   snackBar.show({ content: e?.message });
        // }
      } finally {
        setLoading(false);
      }
    },
    [axiosErrorHandler, navigation, route.params.preToken, snackBar]
  );

  return React.useMemo(
    () => ({
      form,
      handleSubmit: form.handleSubmit(handleSubmit),
      loading,
    }),
    [form, handleSubmit, loading]
  );
};

export default useForm;
